import React, { useContext, useEffect, useState } from 'react';
import { graphql } from 'react-relay';

import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { useTwcg2304ImportInOnboarding } from '@confluence/experiment-twcg-2304-import-in-onboarding/entry-points/useTwcg2304ImportInOnboarding.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';

import { deletePage } from '../../services/DocImportService.ts';
import type {
	DeletePagesActionMutation as DeletePagesActionMutationData,
	DeletePagesActionMutationVariables,
} from '../../hooks/__types__/DeletePagesActionMutation.ts';
import type { ProcessErrors } from '../../config/processErrors.ts';
import { ImportStateContext } from '../ImportStateContextProvider.tsx';

import { ImportModalTypes } from './ImportModalTypes.ts';
import { DiscardModal } from './DiscardModal.tsx';
import { FileSizeModal } from './FileSizeModal.tsx';
import { FileTypeModal } from './FileTypeModal.tsx';
import { BulkLimitModal } from './BulkLimitModal.tsx';
import { RestrictionWarningModal } from './RestrictionWarningModal.tsx';
import { ProgressModal } from './ProgressModal/ProgressModal.tsx';
import { ProgressStatus } from './ProgressModal/progress-types.ts';
import { CommonErrorModal } from './CommonErrorModal.tsx';
import { ProcessWarningPopup } from './ProcessWarningPopup.tsx';
import { StrictFormatModal } from './StrictFormatModal.tsx';
import { PayloadTooLargeModal } from './PayloadTooLargeModal.tsx';
import { OutcomeUnknownModal } from './OutcomeUnknownModal.tsx';

export const ImportModals: React.FC<{
	onReimport: () => void;
	goToCreatedDraftPage: () => void;
	setDraftDiscard: (isDraftDiscardRequired: boolean) => void;
	isDraftDiscard: boolean;
	preserveSourcePage: boolean;
	contentId: string;
	elementProcessingError: ProcessErrors | null;
	setElementProcessingError: (error: ProcessErrors | null) => void;
	goToCreatedPage: () => void;
	bulkParentPageId: string | null;
	setBulkParentPageId: (id: null) => void;
	setIsCreatingDraftCancelled: (val: boolean) => void;
	isCancelledDocumentsDeleted: boolean;
}> = ({
	setDraftDiscard,
	onReimport,
	goToCreatedDraftPage,
	isDraftDiscard,
	preserveSourcePage,
	contentId,
	elementProcessingError,
	setElementProcessingError,
	goToCreatedPage,
	bulkParentPageId,
	setBulkParentPageId,
	setIsCreatingDraftCancelled,
	isCancelledDocumentsDeleted,
}) => {
	const { closeModal, showModal, modalStack, docs } = useContext(ImportStateContext);

	const [deletePagesActionMutation] = useMutationInterop<
		DeletePagesActionMutationData,
		DeletePagesActionMutationVariables
	>(graphql`
		mutation ImportModalsDeletePagesActionMutation($input: [DeletePagesInput]!) {
			deletePages(input: $input) {
				taskId
				status
			}
		}
	`);

	const isLivePage = useIsCurrentPageLive();
	const { isEnabled: isUploadInOnboardingEnabled, loading: isUploadInOnboardingLoading } =
		useTwcg2304ImportInOnboarding();
	const shouldCreateAsLivePage =
		isLivePage || (!isUploadInOnboardingLoading && isUploadInOnboardingEnabled);

	const [isDisabledCancel, setIsDisabledCancel] = useState(false);
	const isProgressCancelDisabled =
		isDisabledCancel || Boolean(modalStack[ImportModalTypes.PROGRESS]?.cancelDisabled);

	//TODO: refactor
	useEffect(() => {
		setIsDisabledCancel(Boolean(bulkParentPageId));
	}, [bulkParentPageId]);

	const closeModalAndReimport = (modalType: ImportModalTypes) => {
		closeModal(modalType);
		// CONFDEV-76270: as onReimport call depends on closeModal call result
		// timer here is used for calling onReimport at the end of the call stack
		// when the state of the ImportStrategyContextComponent is fully updated
		setTimeout(() => {
			onReimport();
		}, 0);
	};

	const handleDiscardModalCreate = (isDraftDiscardRequired: boolean) => {
		setDraftDiscard(isDraftDiscardRequired);
		closeModalAndReimport(
			/* webpackChunkName: "loadable-ImportModalTypesDISCARD" */ ImportModalTypes.DISCARD,
		);
	};

	const handleDiscardDraft = async () => {
		// contentId is empty when the import was started without an existing draft
		// (e.g. the onboarding CTA in the space side navigation) — nothing to discard.
		if (!preserveSourcePage && isDraftDiscard && contentId) {
			try {
				const subType = shouldCreateAsLivePage ? 'live' : '';
				await deletePage(contentId, { subType });
			} catch (error) {
				return false;
			}
		}
	};

	const handleProgressModalFinishDraft = async () => {
		void handleDiscardDraft();
		if (elementProcessingError) {
			closeModal(ImportModalTypes.PROGRESS);
			showModal(ImportModalTypes.PROCESS_ERROR);
			return;
		}

		closeModal(ImportModalTypes.PROGRESS);
		goToCreatedDraftPage();
	};

	const handleDeleteUploadedPages = async () => {
		try {
			if (bulkParentPageId) {
				setBulkParentPageId(null);

				await deletePagesActionMutation({
					variables: {
						input: [
							{
								pageID: parseInt(bulkParentPageId, 10),
								areChildrenIncluded: true,
								isSelected: true,
							},
						],
					},
				});
			}
		} catch (e) {
			return e;
		}
	};

	const handleProgressModalCancel = () => {
		if (isProgressCancelDisabled) {
			return;
		}

		// abort import of the pages that currently in process
		docs.forEach((doc) => doc.controller?.abort());
		// delete pages that have been imported
		void handleDeleteUploadedPages();
		if (
			modalStack[ImportModalTypes.PROGRESS].status !== ProgressStatus.ERROR &&
			!modalStack[ImportModalTypes.PROGRESS].skipDeferredDraftCleanupOnCancel
		) {
			setIsCreatingDraftCancelled(true);
		}
		closeModal(ImportModalTypes.PROGRESS);
	};

	const handleProcessWarningPopupSubmit = () => {
		closeModal(ImportModalTypes.PROCESS_ERROR);
		setElementProcessingError(null);
		goToCreatedDraftPage();
	};

	const handleProgressModalFinishPage = async () => {
		void handleDiscardDraft();
		// Close explicitly rather than relying on navigation unmounting this tree. Entry
		// points outside the editor (e.g. the side navigation) stay mounted across the
		// route change, which would otherwise leave the progress modal open.
		closeModal(ImportModalTypes.PROGRESS);
		goToCreatedPage();
	};

	return (
		<>
			{modalStack[ImportModalTypes.DISCARD] && (
				<DiscardModal
					onCreate={handleDiscardModalCreate}
					onClose={() => closeModal(ImportModalTypes.DISCARD)}
				/>
			)}
			{modalStack[ImportModalTypes.FILE_SIZE] && (
				<FileSizeModal
					fileData={modalStack[ImportModalTypes.FILE_SIZE]}
					onSubmit={() =>
						closeModalAndReimport(
							/* webpackChunkName: "loadable-ImportModalTypesFILESIZE" */ ImportModalTypes.FILE_SIZE,
						)
					}
					onClose={() => closeModal(ImportModalTypes.FILE_SIZE)}
				/>
			)}
			{modalStack[ImportModalTypes.FILE_TYPE] && (
				<FileTypeModal
					onSubmit={() =>
						closeModalAndReimport(
							/* webpackChunkName: "loadable-ImportModalTypesFILETYPE" */ ImportModalTypes.FILE_TYPE,
						)
					}
					onClose={() => closeModal(ImportModalTypes.FILE_TYPE)}
				/>
			)}
			{modalStack[ImportModalTypes.PROGRESS] && (
				<ProgressModal
					status={modalStack[ImportModalTypes.PROGRESS].status}
					isDisabledCancel={isProgressCancelDisabled}
					onCancel={handleProgressModalCancel}
					onFinishDraft={handleProgressModalFinishDraft}
					onFinishPage={handleProgressModalFinishPage}
					onRetry={() =>
						closeModalAndReimport(
							/* webpackChunkName: "loadable-ImportModalTypesPROGRESS" */ ImportModalTypes.PROGRESS,
						)
					}
					bulkParentPageId={bulkParentPageId}
					isCancelledDocumentsDeleted={isCancelledDocumentsDeleted}
					isAutoLandEnabled={!isUploadInOnboardingLoading && isUploadInOnboardingEnabled}
					warnings={modalStack[ImportModalTypes.PROGRESS].warnings}
				/>
			)}
			{modalStack[ImportModalTypes.OUTCOME_UNKNOWN] && (
				<OutcomeUnknownModal
					attemptId={modalStack[ImportModalTypes.OUTCOME_UNKNOWN].attemptId}
					onClose={() => closeModal(ImportModalTypes.OUTCOME_UNKNOWN)}
				/>
			)}
			{modalStack[ImportModalTypes.UPLOAD_ERROR] && (
				<CommonErrorModal
					onClose={() => closeModal(ImportModalTypes.UPLOAD_ERROR)}
					onSubmit={() =>
						closeModalAndReimport(
							/* webpackChunkName: "loadable-ImportModalTypesUPLOADERROR" */ ImportModalTypes.UPLOAD_ERROR,
						)
					}
					headText={modalStack[ImportModalTypes.UPLOAD_ERROR].headText}
					bodyText={modalStack[ImportModalTypes.UPLOAD_ERROR].bodyText}
					submitText={modalStack[ImportModalTypes.UPLOAD_ERROR].submitText}
					modalWidth={modalStack[ImportModalTypes.UPLOAD_ERROR].modalWidth}
				/>
			)}
			{modalStack[ImportModalTypes.PROCESS_ERROR] && (
				<ProcessWarningPopup
					errorType={elementProcessingError!}
					onSubmit={handleProcessWarningPopupSubmit}
					onClose={() => {
						closeModal(ImportModalTypes.PROCESS_ERROR);
						setElementProcessingError(null);
					}}
				/>
			)}
			{modalStack[ImportModalTypes.RESTRICTION] && (
				<RestrictionWarningModal
					onClose={modalStack[ImportModalTypes.RESTRICTION].onCancel}
					onSubmit={modalStack[ImportModalTypes.RESTRICTION].onConfirm}
				/>
			)}
			{modalStack[ImportModalTypes.BULK_LIMIT] && (
				<BulkLimitModal
					onClose={modalStack[ImportModalTypes.BULK_LIMIT].onCancel}
					onRetry={modalStack[ImportModalTypes.BULK_LIMIT].onConfirm}
				/>
			)}
			{modalStack[ImportModalTypes.STRICT_FORMAT_ERROR] && (
				<StrictFormatModal
					onClose={() => closeModal(ImportModalTypes.STRICT_FORMAT_ERROR)}
					onSubmit={() =>
						closeModalAndReimport(
							/* webpackChunkName: "loadable-ImportModalTypesSTRICTFORMATERROR" */ ImportModalTypes.STRICT_FORMAT_ERROR,
						)
					}
				/>
			)}
			{modalStack[ImportModalTypes.PAYLOAD_TOO_LARGE] && (
				<PayloadTooLargeModal
					onClose={() => closeModal(ImportModalTypes.PAYLOAD_TOO_LARGE)}
					onSubmit={() =>
						closeModalAndReimport(
							/* webpackChunkName: "loadable-ImportModalTypesPAYLOADTOOLARGE" */ ImportModalTypes.PAYLOAD_TOO_LARGE,
						)
					}
				/>
			)}
		</>
	);
};
